import { z } from 'zod';

import { AlertStateSchema } from '../enums.js';

import { IsoDateTimeSchema, UuidSchema } from './common.js';

// messaging-service: in-app notifications and owner-linked threads (spec §5.5, BR-048).

export const NotificationSchema = z.object({
  id: UuidSchema,
  alertId: UuidSchema.nullable(),
  type: z.string(),
  text: z.string(),
  createdAt: IsoDateTimeSchema,
  readAt: IsoDateTimeSchema.nullable(),
});
export type Notification = z.infer<typeof NotificationSchema>;

export const NotificationsResponseSchema = z.object({
  notifications: z.array(NotificationSchema),
});
export type NotificationsResponse = z.infer<typeof NotificationsResponseSchema>;

export const NotificationIdParamsSchema = z.object({ id: UuidSchema });

export const NotificationsQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

/** "I sent this myself": the owner links a thread Baseline didn't originate. */
export const LinkThreadRequestSchema = z.object({
  alertIds: z.array(UuidSchema).min(1),
  providerThreadId: z.string().min(1),
});
export type LinkThreadRequest = z.infer<typeof LinkThreadRequestSchema>;

export const ThreadSchema = z.object({
  id: UuidSchema,
  origin: z.enum(['originated', 'owner_linked']),
  providerThreadId: z.string(),
  alertIds: z.array(UuidSchema),
});
export type Thread = z.infer<typeof ThreadSchema>;
export const ThreadResponseSchema = z.object({ thread: ThreadSchema });
export type ThreadResponse = z.infer<typeof ThreadResponseSchema>;

/**
 * `POST /internal/replies/poll` on messaging-service, step 1 of the watch cycle (BR-041): read
 * the threads, classify new replies, and return the ids of the `messaging.reply.classified`
 * events it wrote, so the agent can wait for alert-service to apply them before any timer.
 */
export const ReadRepliesResponseSchema = z.object({
  repliesRead: z.number().int().nonnegative(),
  eventIds: z.array(UuidSchema),
});
export type ReadRepliesResponse = z.infer<typeof ReadRepliesResponseSchema>;

// ── alert-service internal reads for messaging-service ──────────────────────────────

/** What messaging-service needs to know about an alert: to classify, verify ownership, demo. */
export const AlertRefSchema = z.object({
  id: UuidSchema,
  code: z.string(),
  assemblyId: UuidSchema,
  configuration: z.string(),
  parentPath: z.string(),
  partPn: z.string(),
  pdmQty: z.number().nullable(),
  state: AlertStateSchema,
  ownerUserId: UuidSchema.nullable(),
});
export type AlertRef = z.infer<typeof AlertRefSchema>;

/** `GET /internal/alerts/refs?ids=a,b` on alert-service. Unknown ids are left out. */
export const AlertRefsQuerySchema = z.object({
  ids: z
    .string()
    .transform((s) => s.split(',').filter((id) => id.length > 0))
    .pipe(z.array(UuidSchema).min(1).max(200)),
});
export const AlertRefsResponseSchema = z.object({ alerts: z.array(AlertRefSchema) });
export type AlertRefsResponse = z.infer<typeof AlertRefsResponseSchema>;

/**
 * `GET /internal/drafts/awaiting-reply` on alert-service: sent emails whose alerts still
 * wait on a reply. The demo's login kickoff uses it for BL-104, whose email was seeded
 * (ADR 0013) and so has no record in messaging-service.
 */
export const SentDraftSchema = z.object({
  draftVersionId: UuidSchema,
  alertIds: z.array(UuidSchema).min(1),
  fromUserId: UuidSchema,
  fromEmail: z.email(),
  to: z.email(),
  subject: z.string(),
  body: z.string(),
  sentAt: IsoDateTimeSchema,
});
export type SentDraft = z.infer<typeof SentDraftSchema>;
export const SentDraftsResponseSchema = z.object({ drafts: z.array(SentDraftSchema) });
export type SentDraftsResponse = z.infer<typeof SentDraftsResponseSchema>;
